/**
 * Servicio de consentimientos informados por evento.
 *
 * Cada miembro inscrito a un evento debe completar un EventConsent antes de poder
 * participar. El doc se guarda con id = `${eventId}_${memberId}` para idempotencia.
 *
 * Status:
 *  - "pending": creado pero sin progreso real
 *  - "in_progress": tiene al menos un documento o firma
 *  - "completed": todos los pasos requeridos firmados + PDF generado
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  collection,
  getDocs,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type {
  EventConsent,
  ConsentParticipantType,
  ConsentFormSnapshot,
  ConsentDocument,
  ConsentSignature,
  ConsentParentRecord,
  ConsentChecklistItem,
  ConsentChecklistItemId,
  EventConsentChecklistConfig,
  Event,
  Person,
  Member,
  EventRegistration,
} from "@/types";

const COLLECTION = "eventConsents";

/**
 * Construye el id determinístico del consentimiento.
 */
export function buildConsentId(eventId: string, memberId: string): string {
  return `${eventId}_${memberId}`;
}

/**
 * Convierte de forma segura cualquier representación de fecha
 * (Timestamp, Date, string, number, {seconds}) a ISO string.
 */
function toIsoSafe(value: unknown): string {
  if (!value) return new Date().toISOString();
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "seconds" in value &&
    typeof (value as { seconds: number }).seconds === "number"
  ) {
    return new Date(
      (value as { seconds: number }).seconds * 1000,
    ).toISOString();
  }
  if (typeof value === "string" || typeof value === "number") {
    return new Date(value).toISOString();
  }
  return new Date().toISOString();
}

/**
 * Elimina recursivamente campos undefined de un objeto plano
 * para que Firestore no rechace el documento.
 */
function sanitize<T extends Record<string, unknown>>(obj: T): T {
  Object.keys(obj).forEach((k) => {
    if (obj[k] === undefined) delete obj[k];
  });
  return obj;
}

/**
 * Calcula la edad del miembro a la fecha de finalización del evento.
 */
export function calculateAgeAtEventEnd(
  birthDate: Date | Timestamp,
  eventEndDate: Date | Timestamp,
): number {
  const birth = new Date(toIsoSafe(birthDate));
  const end = new Date(toIsoSafe(eventEndDate));
  let age = end.getFullYear() - birth.getFullYear();
  const m = end.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && end.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Determina si el participante es menor de edad (< 18 años) a la fecha fin del evento.
 */
export function determineParticipantType(
  birthDate: Date | Timestamp,
  eventEndDate: Date | Timestamp,
): ConsentParticipantType {
  return calculateAgeAtEventEnd(birthDate, eventEndDate) < 18
    ? "minor"
    : "adult";
}

/**
 * Construye el snapshot inmutable de datos auto-fill al iniciar/firmar el consentimiento.
 */
export function buildFormSnapshot(args: {
  member: Member;
  person: Person;
  event: Event;
  clubName: string;
  churchName?: string;
  districtName?: string;
  associationName: string;
  boardVoteNumber?: string;
  boardVoteDate?: string;
}): ConsentFormSnapshot {
  const { member: _member, person, event } = args;
  const raw: Record<string, unknown> = {
    memberFullName: person.fullName,
    memberIdDocumentType: person.idDocumentType,
    memberIdDocumentNumber: person.idDocumentNumber,
    memberBirthDate: toIsoSafe(person.birthDate),
    memberAddress: person.address,
    memberHealthInsurance: person.healthInsurance,
    memberMedicalConditions: person.medicalConditions,
    memberAllergies: person.allergies,
    memberMedications: person.medications,
    memberNoMedicalConditions: person.noMedicalConditions,
    memberIdExpeditionCity: person.idExpeditionCity,
    eventName: event.name,
    eventStartDate: toIsoSafe(event.startDate),
    eventEndDate: toIsoSafe(event.endDate),
    eventLocationName: event.location.name,
    eventLocationCity: event.location.city,
    eventLocationAddress: event.location.address,
    clubName: args.clubName,
    churchName: args.churchName,
    districtName: args.districtName,
    associationName: args.associationName,
    boardVoteNumber: args.boardVoteNumber,
    boardVoteDate: args.boardVoteDate,
  };
  // Eliminar campos undefined — Firestore no acepta undefined
  return sanitize(raw) as unknown as ConsentFormSnapshot;
}

/**
 * Obtiene el consentimiento por id determinístico.
 */
export async function getConsentById(
  eventId: string,
  memberId: string,
): Promise<EventConsent | null> {
  const ref = doc(db, COLLECTION, buildConsentId(eventId, memberId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as EventConsent;
}

/**
 * Obtiene todos los consentimientos de un evento.
 */
export async function getEventConsents(
  eventId: string,
): Promise<EventConsent[]> {
  const q = query(collection(db, COLLECTION), where("eventId", "==", eventId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EventConsent);
}

/** Extrae el token de descarga de una URL de Firebase Storage. */
function extractDownloadToken(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).searchParams.get("token");
  } catch {
    return null;
  }
}

/** Extrae la ruta del objeto (persons/<id>/x.enc) de una URL de Storage. */
function extractStoragePath(url?: string): string | null {
  if (!url) return null;
  try {
    const m = new URL(url).pathname.match(/\/o\/(.+)$/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/**
 * True si `existingRef` apunta al MISMO archivo de Storage que `freshRef` (del
 * perfil) pero con un token de descarga distinto → el archivo fue re-subido
 * (token y llave nuevos) y la referencia guardada quedó muerta (403). Solo
 * aplica a refs que comparten la ruta canónica del Person; NO afecta archivos
 * subidos durante el check-in (que viven en consentFolder).
 */
function isSamePathButRotated(
  existingRef?: ConsentDocument,
  freshRef?: ConsentDocument,
): boolean {
  if (!existingRef?.url || !freshRef?.url) return false;
  const ePath = existingRef.path || extractStoragePath(existingRef.url);
  const fPath = freshRef.path || extractStoragePath(freshRef.url);
  if (!ePath || ePath !== fPath) return false;
  return (
    extractDownloadToken(existingRef.url) !== extractDownloadToken(freshRef.url)
  );
}

/**
 * Crea (o reemplaza) un consentimiento en estado pending.
 */
export async function initConsent(args: {
  event: Event;
  registration: EventRegistration;
  member: Member;
  person: Person;
  clubName: string;
  associationName: string;
  churchName?: string;
  districtName?: string;
  boardVoteNumber?: string;
  boardVoteDate?: string;
  userId?: string;
}): Promise<EventConsent> {
  const { event, registration, member, person, clubName, associationName } =
    args;
  const id = buildConsentId(event.id, member.id);
  const existing = await getConsentById(event.id, member.id);

  const participantType = determineParticipantType(
    person.birthDate,
    event.endDate,
  );
  const formSnapshot = buildFormSnapshot({
    member,
    person,
    event,
    clubName,
    churchName: args.churchName,
    districtName: args.districtName,
    associationName,
    boardVoteNumber: args.boardVoteNumber,
    boardVoteDate: args.boardVoteDate,
  });

  // Pre-poblar documentos desde el perfil de la persona si ya existen.
  // Evita pedirlos de nuevo en el check-in: el modal los mostrará como cargados.
  const now = Timestamp.now();
  const idDocumentFront: ConsentDocument | undefined = person.documentFrontUrl
    ? {
        url: person.documentFrontUrl,
        path: person.documentFrontPath || "",
        contentType: person.documentFrontContentType || "image/jpeg",
        encryption: person.documentFrontEncryption,
        uploadedAt: now,
        fileName: "documento-anverso",
      }
    : undefined;
  const idDocumentBack: ConsentDocument | undefined = person.documentBackUrl
    ? {
        url: person.documentBackUrl,
        path: person.documentBackPath || "",
        contentType: person.documentBackContentType || "image/jpeg",
        encryption: person.documentBackEncryption,
        uploadedAt: now,
        fileName: "documento-reverso",
      }
    : undefined;
  const epsCertificate: ConsentDocument | undefined = person.epsDocumentUrl
    ? {
        url: person.epsDocumentUrl,
        path: person.epsDocumentPath || "",
        contentType: person.epsDocumentContentType || "image/jpeg",
        encryption: person.epsDocumentEncryption,
        uploadedAt: now,
        fileName: "eps",
      }
    : undefined;

  // La FOTO del perfil también viaja al check-in. Faltaba, y con ella faltaba
  // un paso de cuatro: el 2026-09-28 había 442 inscritos con documentos en su
  // ficha que el evento no veía, y aunque se sincronizaran quedaban en 3/4
  // porque la foto no se copiaba. A diferencia del documento y la EPS, la foto
  // de perfil no va encriptada.
  const participantPhoto: ConsentDocument | undefined = person.photoUrl
    ? {
        url: person.photoUrl,
        path: person.photoPath || "",
        contentType: "image/jpeg",
        uploadedAt: now,
        fileName: "foto-participante",
      }
    : undefined;

  // Si ya existe el consent, sincronizamos solo los docs faltantes desde el
  // perfil (no sobreescribimos los que el consent ya tenga, que pueden ser
  // versiones más recientes subidas durante el check-in).
  if (existing) {
    const patch: Record<string, unknown> = {};
    if (!existing.idDocumentFront && idDocumentFront)
      patch.idDocumentFront = idDocumentFront;
    if (!existing.idDocumentBack && idDocumentBack)
      patch.idDocumentBack = idDocumentBack;
    if (!existing.epsCertificate && epsCertificate)
      patch.epsCertificate = epsCertificate;
    if (!existing.participantPhoto && participantPhoto)
      patch.participantPhoto = participantPhoto;

    // Refrescar referencias a documentos que apuntan al MISMO archivo del Person
    // pero con token rotado: la persona re-subió el doc (token y llave nuevos,
    // sobrescribiendo el archivo) y la referencia del consent quedó muerta (403
    // al desencriptar). Copiamos url+path+encryption frescos del perfil. No se
    // toca el PDF firmado ni las firmas; los docs de soporte no son evidencia
    // de firma. No afecta archivos subidos en el check-in (otra ruta).
    if (isSamePathButRotated(existing.idDocumentFront, idDocumentFront))
      patch.idDocumentFront = idDocumentFront;
    if (isSamePathButRotated(existing.idDocumentBack, idDocumentBack))
      patch.idDocumentBack = idDocumentBack;
    if (isSamePathButRotated(existing.epsCertificate, epsCertificate))
      patch.epsCertificate = epsCertificate;

    // Auto-sanar el snapshot mientras el consentimiento NO esté firmado ni
    // finalizado. Cubre el caso en que se corrigen datos del Person (p.ej. la
    // fecha de nacimiento mal digitada) DESPUÉS de haber creado el consent: el
    // snapshot (participantType, memberBirthDate, nombre, documento…) quedaba
    // congelado y desactualizado. Si ya hay firmas/PDF, NO se toca (es evidencia).
    const isLocked =
      !!existing.dataConsent ||
      !!existing.participantSignature ||
      (existing.parentSignatures?.length ?? 0) > 0 ||
      !!existing.pdfDocument ||
      !!existing.completedAt ||
      existing.status === "completed" ||
      !!existing.voidedBySwap;
    if (!isLocked) {
      if (existing.participantType !== participantType) {
        patch.participantType = participantType;
      }
      const newSnap = formSnapshot as unknown as Record<string, unknown>;
      const oldSnap = (existing.formSnapshot ?? {}) as unknown as Record<
        string,
        unknown
      >;
      const snapshotChanged = Object.keys(newSnap).some(
        (k) => oldSnap[k] !== newSnap[k],
      );
      if (snapshotChanged) {
        // Mezcla: refresca los campos derivados del Person/evento y conserva
        // cualquier campo existente que el snapshot nuevo no incluya.
        patch.formSnapshot = { ...existing.formSnapshot, ...formSnapshot };
      }
    }

    if (Object.keys(patch).length > 0) {
      await updateDoc(doc(db, COLLECTION, id), {
        ...sanitize(patch),
        updatedAt: serverTimestamp(),
        updatedBy: args.userId || null,
      });
      return (await getConsentById(event.id, member.id)) as EventConsent;
    }
    return existing;
  }

  const data: Omit<EventConsent, "createdAt" | "updatedAt"> = {
    id,
    eventId: event.id,
    eventName: event.name,
    memberId: member.id,
    personId: person.id,
    registrationId: registration.id,
    clubId: member.clubId,
    associationId: member.associationId || event.organizerId,
    participantType,
    status: "pending",
    formSnapshot,
    ...(idDocumentFront ? { idDocumentFront } : {}),
    ...(idDocumentBack ? { idDocumentBack } : {}),
    ...(epsCertificate ? { epsCertificate } : {}),
    ...(participantPhoto ? { participantPhoto } : {}),
  };

  await setDoc(doc(db, COLLECTION, id), {
    ...sanitize({ ...data }),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: args.userId || null,
    updatedBy: args.userId || null,
  });

  // Releer para devolver con timestamps materializados
  return (await getConsentById(event.id, member.id)) as EventConsent;
}

/**
 * Marca el consentimiento de un miembro como invalidado por un intercambio de
 * integrante (swap). El doc NO se borra (es evidencia); solo se marca para que
 * deje de contar como consentimiento vigente. Idempotente: si el miembro nunca
 * inició su consentimiento, no hace nada.
 */
export async function voidConsentBySwap(
  eventId: string,
  memberId: string,
  userId?: string,
): Promise<void> {
  const ref = doc(db, COLLECTION, buildConsentId(eventId, memberId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  await updateDoc(ref, {
    voidedBySwap: true,
    voidedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
}

/**
 * Aplica un paso del check-in y RECALCULA el estado del consentimiento contra
 * la configuración del evento.
 *
 * Antes cada setter escribía `status: "in_progress"` cableado y solo la foto
 * (que se suponía el último paso) evaluaba si ya estaba todo. Bastaba con
 * subir la foto y después cualquier otro documento para que el sello se
 * perdiera y no volviera nunca: el 2026-08-11 había 31 consentimientos con
 * todos sus pasos hechos que el sistema seguía mostrando como pendientes, y
 * a esa gente se le seguían pidiendo documentos que ya había entregado.
 *
 * Ahora el orden de los pasos da igual: el estado sale siempre de los datos.
 */
async function aplicarPaso(
  eventId: string,
  memberId: string,
  patch: Record<string, unknown>,
  userId?: string,
): Promise<void> {
  const ref = doc(db, COLLECTION, buildConsentId(eventId, memberId));
  const [snap, eventSnap] = await Promise.all([
    getDoc(ref),
    getDoc(doc(db, "events", eventId)),
  ]);
  const actual = snap.exists() ? (snap.data() as EventConsent) : null;
  const config = eventSnap.exists()
    ? (eventSnap.data() as Event).consentChecklistConfig
    : undefined;
  // El merge es superficial a propósito: los patches son de primer nivel
  // (idDocumentFront, epsCertificate…) salvo formSnapshot, que el caller
  // manda ya combinado.
  const listo = isConsentCompleted(
    actual ? ({ ...actual, ...patch } as EventConsent) : null,
    config,
  );
  await updateDoc(ref, {
    ...patch,
    status: listo ? "completed" : "in_progress",
    // completedAt se pone la primera vez y no se vuelve a mover.
    ...(listo && !actual?.completedAt ? { completedAt: Timestamp.now() } : {}),
    updatedAt: serverTimestamp(),
    updatedBy: userId || null,
  });
}

/**
 * Setea el documento de identidad cargado.
 * @deprecated Usar setConsentIdDocumentFront / Back.
 */
export async function setConsentIdDocument(
  eventId: string,
  memberId: string,
  document: ConsentDocument,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    idDocument: document,
    },
    userId,
  );
}

/** Anverso del documento de identidad */
export async function setConsentIdDocumentFront(
  eventId: string,
  memberId: string,
  document: ConsentDocument,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    idDocumentFront: document,
    },
    userId,
  );
}

/** Reverso del documento de identidad */
export async function setConsentIdDocumentBack(
  eventId: string,
  memberId: string,
  document: ConsentDocument,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    idDocumentBack: document,
    },
    userId,
  );
}

/**
 * Setea el certificado EPS cargado.
 */
export async function setConsentEpsCertificate(
  eventId: string,
  memberId: string,
  document: ConsentDocument,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    epsCertificate: document,
    },
    userId,
  );
}

/**
 * Captura los datos médicos declarados + datos de viaje (ciudades) y los
 * persiste DENTRO del `formSnapshot` del consentimiento. Esto los hace
 * inmutables para el PDF generado, aunque luego cambien en `Person`.
 *
 * El UI llama esto desde el paso "Datos médicos y de viaje". Adicionalmente,
 * el caller debe propagar a `Person` (vía `personService.updateMedicalInfo`)
 * para no volver a preguntarlos en el siguiente evento.
 */
export async function setConsentMedicalAndTravel(
  eventId: string,
  memberId: string,
  data: {
    healthInsurance?: string;
    medicalConditions?: string;
    allergies?: string;
    medications?: string;
    noMedicalConditions?: boolean;
    idExpeditionCity?: string;
    departureCity?: string;
    returnCity?: string;
  },
  userId?: string,
): Promise<void> {
  const ref = doc(db, COLLECTION, buildConsentId(eventId, memberId));
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Consent not found");
  const current = snap.data() as EventConsent;
  const merged: ConsentFormSnapshot = {
    ...current.formSnapshot,
    memberHealthInsurance:
      data.healthInsurance ?? current.formSnapshot.memberHealthInsurance,
    memberMedicalConditions:
      data.medicalConditions ?? current.formSnapshot.memberMedicalConditions,
    memberAllergies: data.allergies ?? current.formSnapshot.memberAllergies,
    memberMedications:
      data.medications ?? current.formSnapshot.memberMedications,
    memberNoMedicalConditions:
      data.noMedicalConditions ??
      current.formSnapshot.memberNoMedicalConditions,
    memberIdExpeditionCity:
      data.idExpeditionCity ?? current.formSnapshot.memberIdExpeditionCity,
    departureCity: data.departureCity ?? current.formSnapshot.departureCity,
    returnCity: data.returnCity ?? current.formSnapshot.returnCity,
  };
  // El snapshot ya viene combinado con el actual: aplicarPaso lo mezcla plano.
  await aplicarPaso(
    eventId,
    memberId,
    { formSnapshot: sanitize(merged as unknown as Record<string, unknown>) },
    userId,
  );
}

/**
 * Marca aceptación del tratamiento de datos. Requiere firma gráfica.
 */
export async function acceptDataConsent(
  eventId: string,
  memberId: string,
  signature: ConsentSignature,
  userAgent?: string,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    dataConsent: {
    accepted: true,
    acceptedAt: Timestamp.now(),
    signature,
    userAgent: userAgent || null,
    },
    },
    userId,
  );
}

/**
 * Setea la firma del participante (adulto).
 */
export async function setParticipantSignature(
  eventId: string,
  memberId: string,
  signature: ConsentSignature,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    participantSignature: signature,
    },
    userId,
  );
}

/**
 * Agrega una firma de padre/acudiente (para menor). Reemplaza el array completo.
 */
export async function setParentSignatures(
  eventId: string,
  memberId: string,
  parents: ConsentParentRecord[],
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    parentSignatures: parents,
    },
    userId,
  );
}

/**
 * Adjunta el PDF final y marca como completed.
 */
/**
 * Guarda el PDF firmado del consentimiento. NO marca el consentimiento como completado:
 * la finalización ocurre al subir la foto del participante (último paso).
 */
export async function completeConsent(
  eventId: string,
  memberId: string,
  pdfDocument: ConsentDocument,
  userId?: string,
): Promise<void> {
  await aplicarPaso(
    eventId,
    memberId,
    {
    pdfDocument,
    },
    userId,
  );
}

/**
 * Lista los consentimientos de un usuario para todas las inscripciones donde es el person/member.
 */
export async function listConsentsByMember(
  memberId: string,
): Promise<EventConsent[]> {
  const q = query(
    collection(db, COLLECTION),
    where("memberId", "==", memberId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EventConsent);
}

/**
 * Lista los consentimientos de un evento (para vistas admin).
 */
export async function listConsentsByEvent(
  eventId: string,
): Promise<EventConsent[]> {
  const q = query(collection(db, COLLECTION), where("eventId", "==", eventId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EventConsent);
}

/**
 * Devuelve true si el participante completó datos médicos y de viaje
 * (firmó EPS, declaró condiciones o "no presento condiciones", y confirmó
 * ciudades). Para mayor de edad también requiere lugar de expedición CC.
 */
export function isMedicalAndTravelDone(consent: EventConsent | null): boolean {
  if (!consent) return false;
  const s = consent.formSnapshot;
  const epsOk = !!(s.memberHealthInsurance && s.memberHealthInsurance.trim());
  const medOk =
    !!s.memberNoMedicalConditions ||
    !!(s.memberMedicalConditions && s.memberMedicalConditions.trim()) ||
    !!(s.memberAllergies && s.memberAllergies.trim()) ||
    !!(s.memberMedications && s.memberMedications.trim());
  const travelOk =
    !!(s.departureCity && s.departureCity.trim()) &&
    !!(s.returnCity && s.returnCity.trim());
  const expCityOk =
    consent.participantType === "minor"
      ? true
      : !!(s.memberIdExpeditionCity && s.memberIdExpeditionCity.trim());
  return epsOk && medOk && travelOk && expCityOk;
}

/**
 * Construye el checklist visual del modal con base en el estado actual del consentimiento.
 *
 * Si el evento tiene `consentChecklistConfig`, los items marcados como `false`
 * en la config se muestran con `required: false` (opcionales, no bloquean el
 * check-in). Los items no listados heredan el default (required=true).
 */
export function buildChecklist(
  consent: EventConsent | null,
  checklistConfig?: EventConsentChecklistConfig,
): ConsentChecklistItem[] {
  const isRequired = (id: ConsentChecklistItemId): boolean =>
    checklistConfig?.[id] !== false;
  return [
    {
      id: "idDocumentFront",
      label: "Documento de identidad (anverso)",
      done: !!consent?.idDocumentFront || !!consent?.idDocument,
      required: isRequired("idDocumentFront"),
    },
    {
      id: "idDocumentBack",
      label: "Documento de identidad (reverso)",
      done: !!consent?.idDocumentBack,
      required: isRequired("idDocumentBack"),
    },
    {
      id: "epsCertificate",
      label: "Certificado de EPS",
      done: !!consent?.epsCertificate,
      required: isRequired("epsCertificate"),
    },
    {
      id: "medicalAndTravel",
      label: "Datos médicos y del viaje",
      done: isMedicalAndTravelDone(consent),
      required: isRequired("medicalAndTravel"),
    },
    {
      id: "dataConsent",
      label: "Autorización de tratamiento de datos",
      done:
        !!consent?.dataConsent?.accepted && !!consent?.dataConsent?.signature,
      required: isRequired("dataConsent"),
    },
    {
      id: "participationConsent",
      label: "Autorización de participación firmada",
      done:
        consent?.participantType === "minor"
          ? (consent?.parentSignatures?.length ?? 0) >= 1 &&
            !!consent?.pdfDocument
          : !!consent?.participantSignature && !!consent?.pdfDocument,
      required: isRequired("participationConsent"),
    },
    {
      id: "participantPhoto",
      label: "Foto del participante",
      done: !!consent?.participantPhoto,
      required: isRequired("participantPhoto"),
    },
  ];
}

/**
 * Guarda la foto del participante. Ya no es "el paso que cierra": como todos
 * los demás, aplica su dato y deja que el estado se recalcule contra la
 * configuración del evento.
 */
export async function setConsentParticipantPhoto(
  eventId: string,
  memberId: string,
  document: ConsentDocument,
  userId?: string,
): Promise<void> {
  await aplicarPaso(eventId, memberId, { participantPhoto: document }, userId);
}

/**
 * Devuelve true si todos los pasos requeridos están completos.
 */
export function isConsentCompleted(
  consent: EventConsent | null,
  checklistConfig?: EventConsentChecklistConfig,
): boolean {
  // Un evento puede apagar TODOS los pasos (no pide check-in). En ese caso no
  // hay nada pendiente, ni siquiera para quien no tiene consentimiento creado:
  // devolver false dejaba a esos inscritos en "pendiente" para siempre y, peor,
  // abría el modal bloqueante de EventConsentGate sin forma de satisfacerlo.
  const items = buildChecklist(consent, checklistConfig);
  const requeridos = items.filter((i) => i.required);
  if (requeridos.length === 0) return true;
  if (!consent) return false;
  return requeridos.every((i) => i.done);
}

/**
 * Sincroniza los documentos del PERFIL (persons) hacia los EventConsents
 * de un club inscrito a un evento. Para cada inscrito:
 *  - Si no existe consent → lo crea (pre-poblado con docs del perfil).
 *  - Si existe pero le faltan docs → patchea solo los campos vacíos.
 *  - No sobreescribe docs ya cargados directamente en el consent.
 *
 * Pensado para arreglar el caso "el director subió los docs en
 * Editar miembro pero no se ven en el evento".
 */
export async function syncClubConsentsFromProfile(args: {
  eventId: string;
  clubId: string;
  userId?: string;
  onProgress?: (done: number, total: number) => void;
}): Promise<{
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
}> {
  const { eventService, eventRegistrationService } = await import("./events");
  const { clubService, associationService } = await import("./organization");
  const { memberService } = await import("./members");

  const event = await eventService.getById(args.eventId);
  if (!event) throw new Error("Evento no encontrado");

  const club = await clubService.getById(args.clubId);
  if (!club) throw new Error("Club no encontrado");

  const association = club.associationId
    ? await associationService.getById(club.associationId)
    : null;
  const associationName = association?.name ?? "";

  const allRegs = await eventRegistrationService.getByEvent(args.eventId);
  const clubRegs = allRegs.filter(
    (r) =>
      r.clubId === args.clubId && !["cancelled", "rejected"].includes(r.status),
  );

  const membersWithPerson = await memberService.getByClubWithPerson(
    args.clubId,
  );
  const memberMap = new Map(membersWithPerson.map((m) => [m.id, m]));

  let created = 0;
  let updated = 0;
  let skipped = 0;
  let errors = 0;

  for (let i = 0; i < clubRegs.length; i++) {
    const reg = clubRegs[i];
    try {
      if (!reg.memberId) {
        skipped++;
        continue;
      }
      const mwp = memberMap.get(reg.memberId);
      if (!mwp || !mwp.person) {
        skipped++;
        continue;
      }
      const before = await getConsentById(args.eventId, reg.memberId);
      const beforeHadDocs =
        !!before?.idDocumentFront ||
        !!before?.idDocumentBack ||
        !!before?.epsCertificate;
      const profileHasDocs =
        !!mwp.person.documentFrontUrl ||
        !!mwp.person.documentBackUrl ||
        !!mwp.person.epsDocumentUrl;

      const after = await initConsent({
        event,
        registration: reg,
        member: mwp,
        person: mwp.person,
        clubName: club.name,
        associationName,
        churchName: undefined,
        districtName: undefined,
        boardVoteNumber: undefined,
        boardVoteDate: undefined,
        userId: args.userId,
      });

      if (!before) {
        created++;
      } else {
        const afterHasMore =
          (!!after.idDocumentFront && !before.idDocumentFront) ||
          (!!after.idDocumentBack && !before.idDocumentBack) ||
          (!!after.epsCertificate && !before.epsCertificate);
        if (afterHasMore) updated++;
        else if (!profileHasDocs && !beforeHadDocs) skipped++;
        else skipped++;
      }
    } catch (e) {
      console.error("[syncClubConsentsFromProfile]", reg.id, e);
      errors++;
    } finally {
      args.onProgress?.(i + 1, clubRegs.length);
    }
  }

  return { total: clubRegs.length, created, updated, skipped, errors };
}
